<template>
  <b-modal
    :visible="visible"
    title="Pré-visualização do PMAU"
    size="xl"
    dialog-class="pmau-viewer-dialog"
    content-class="pmau-viewer-content"
    body-class="pmau-viewer-body"
    hide-footer
    @change="(v) => $emit('change', v)"
    @show="Load"
    @shown="OnResize"
    @hidden="Reset"
  >
    <div class="pmau-viewer-toolbar">
      <div class="pmau-zoom">
        <button
          class="btn btn-sm btn-pmau-zoom"
          :disabled="!url || zoom <= minZoom"
          title="Diminuir zoom"
          @click="StepZoom(-1)"
        >
          <i class="fa fa-search-minus"></i>
        </button>
        <span class="pmau-zoom-value">{{ Math.round(zoom * 100) }}%</span>
        <button
          class="btn btn-sm btn-pmau-zoom"
          :disabled="!url || zoom >= maxZoom"
          title="Aumentar zoom"
          @click="StepZoom(1)"
        >
          <i class="fa fa-search-plus"></i>
        </button>
        <button
          class="btn btn-sm btn-pmau-zoom ml-2"
          :class="{ active: fitMode === 'page' }"
          :disabled="!url"
          title="Uma página inteira na tela"
          @click="SetFit('page')"
        >
          <i class="fa fa-file mr-1"></i> Página
        </button>
        <button
          class="btn btn-sm btn-pmau-zoom"
          :class="{ active: fitMode === 'width' }"
          :disabled="!url"
          title="Página na largura da tela"
          @click="SetFit('width')"
        >
          <i class="fa fa-arrows-alt-h mr-1"></i> Largura
        </button>
      </div>
    </div>

    <div v-if="loading" class="pmau-viewer-state">
      <RotateSquare size="60px" class="animated fadeIn"></RotateSquare>
    </div>

    <div v-else-if="erro" class="pmau-viewer-state">
      <i class="fa fa-exclamation-circle fa-3x mb-3 text-warning"></i>
      <p class="mb-3">{{ erro }}</p>
      <button class="btn btn-sm btn-secondary" @click="Load">
        <i class="fa fa-redo mr-1"></i> Tentar novamente
      </button>
    </div>

    <!-- Blob URL (não srcdoc): os links do Sumário (#ancora) navegam dentro do próprio
         documento. Sandbox sem scripts: o HTML salvo não executa nada. -->
    <iframe
      v-else-if="url"
      ref="frame"
      :key="url"
      :src="url"
      sandbox="allow-same-origin"
      class="pmau-viewer-frame"
      title="Pré-visualização do PMAU"
      @load="OnFrameLoad"
    ></iframe>
  </b-modal>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauService from "../../servico/PmauService";

// Espaço em volta das folhas no documento (padding do body + margem entre folhas, ver
// app/generation/preview.py) — descontado no "ajustar à página/largura".
var FRAME_GUTTER = 48;
var ZOOM_STEP = 0.1;

export default {
  name: "PmauHtmlPreview",
  components: { RotateSquare },
  model: { prop: "visible", event: "change" },
  props: {
    visible: { type: Boolean, default: false },
    contaId: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      erro: null,
      url: null,
      zoom: 1,
      minZoom: 0.25,
      maxZoom: 3,
      // "page" | "width" | null (zoom manual). Os ajustes acompanham o redimensionamento.
      // Sempre abre com uma página inteira na tela (ver Reset).
      fitMode: "page"
    };
  },
  mounted() {
    window.addEventListener("resize", this.OnResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.OnResize);
    this.RevokeUrl();
  },
  methods: {
    async Load() {
      this.loading = true;
      this.erro = null;
      try {
        var res = await PmauService.GetPmauDocumentPreview(this.contaId);
        this.RevokeUrl();
        this.url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      } catch (e) {
        this.erro = "Erro ao montar a pré-visualização do PMAU.";
      } finally {
        this.loading = false;
      }
    },

    FrameDoc() {
      var frame = this.$refs.frame;
      return frame && frame.contentDocument ? frame.contentDocument : null;
    },

    OnFrameLoad() {
      var doc = this.FrameDoc();
      if (!doc) return;
      // Ctrl + roda do mouse dentro do documento controla o zoom (sem zoom do navegador).
      doc.addEventListener("wheel", this.OnWheel, { passive: false });
      if (this.fitMode) {
        this.SetFit(this.fitMode);
        // Reaplica após o layout assentar (a folha pode medir 0 no próprio evento de load).
        window.requestAnimationFrame(() => this.OnResize());
      } else {
        this.ApplyZoom(this.zoom);
      }
    },

    OnWheel(e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      this.StepZoom(e.deltaY < 0 ? 1 : -1);
    },

    OnResize() {
      if (this.url && this.fitMode) this.SetFit(this.fitMode);
    },

    StepZoom(direction) {
      this.fitMode = null;
      var next = Math.round((this.zoom + direction * ZOOM_STEP) * 10) / 10;
      this.ApplyZoom(next);
    },

    // Tamanho da folha em 100% (1ª folha = capa, sempre com a altura exata de uma página).
    SheetSize() {
      var doc = this.FrameDoc();
      var sheet = doc && doc.querySelector(".sheet");
      if (!sheet) return null;
      var current = doc.documentElement.style.zoom;
      doc.documentElement.style.zoom = "1";
      var rect = sheet.getBoundingClientRect();
      doc.documentElement.style.zoom = current;
      return { width: rect.width, height: rect.height };
    },

    SetFit(mode) {
      var frame = this.$refs.frame;
      var size = this.SheetSize();
      if (!frame || !size || !size.width || !size.height) return;
      this.fitMode = mode;
      var byWidth = (frame.clientWidth - FRAME_GUTTER) / size.width;
      var byHeight = (frame.clientHeight - FRAME_GUTTER) / size.height;
      this.ApplyZoom(mode === "page" ? Math.min(byWidth, byHeight) : byWidth);
    },

    ApplyZoom(value) {
      var zoom = Math.min(this.maxZoom, Math.max(this.minZoom, value));
      var doc = this.FrameDoc();
      if (doc) {
        // Mantém o ponto de leitura: mesma fração do documento no topo da tela.
        var el = doc.scrollingElement || doc.documentElement;
        var ratio = el.scrollHeight ? el.scrollTop / el.scrollHeight : 0;
        doc.documentElement.style.zoom = String(zoom);
        el.scrollTop = ratio * el.scrollHeight;
      }
      this.zoom = zoom;
    },

    RevokeUrl() {
      if (this.url) URL.revokeObjectURL(this.url);
      this.url = null;
    },

    Reset() {
      this.RevokeUrl();
      this.erro = null;
      this.fitMode = "page";
      this.zoom = 1;
    }
  }
};
</script>

<style scoped>
.pmau-viewer-state {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 2rem;
}
.pmau-viewer-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #dee2e6;
}
.pmau-viewer-frame {
  flex: 1 1 auto;
  width: 100%;
  border: 0;
  min-height: 0;
}
.pmau-zoom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  margin: 0 auto;
}
.pmau-zoom-value {
  min-width: 3.5rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.btn-pmau-zoom {
  background-color: #546e7a;
  border-color: #546e7a;
  color: #fff;
}
.btn-pmau-zoom:hover:not(:disabled),
.btn-pmau-zoom.active {
  background-color: #37474f;
  border-color: #37474f;
  color: #fff;
}
.btn-pmau-zoom:disabled {
  opacity: 0.65;
}
</style>
